import { ZodIssue } from 'zod'
import formSchema, { TSchemasKey } from '../lib/FormSchema.ts'
import tippy from 'tippy.js'
import 'tippy.js/dist/tippy.css'
import 'tippy.js/animations/scale.css'
import handleCaptcha from '../plugins/PluginRecaptcha.ts'

enum EventType {
  SUBMIT = 'SUBMIT',
  CHANGE = 'CHANGE',
}

interface IConfig {
  formSelector: string
  inputSelector: string
  submitSelector: string
  errorClass: string
  errorType: 'tooltip' | 'text'
  errorShow: 'all' | 'first'
}

const DEFAULT_CONFIG: IConfig = {
  formSelector: 'data-form', // must be a form html element
  inputSelector: 'data-form-input', // container element for input
  submitSelector: 'data-form-submit', // button element with type="submit"
  errorClass: 'form-error', // error element
  errorType: 'tooltip', // error type
  errorShow: 'first', // show all or only the first error
}

const globalConfig = structuredClone(DEFAULT_CONFIG)

const errorSvg = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="#ffffff" class="w-6 h-6">
<path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
</svg>`

const handleSubmit = (form: HTMLFormElement) => {
  form.onsubmit = (e) => {
    e.preventDefault()

    const formName = form.getAttribute(globalConfig.formSelector)

    if (formName === null) return

    const inputContainers = form.querySelectorAll(
      `[${globalConfig.inputSelector}]`,
    )

    const inputs = getInputs(inputContainers)

    const formData = prepareFormData(inputs)

    console.log(formData)

    const result = validateForm(formName, formData)

    console.log(result)

    if (result === true) {
      clearErrors(inputContainers)

      if (form.getAttribute('data-catpcha')) {
        handleCaptcha(form)
      }

      return form.submit()
    }

    handleErrors(inputContainers, result, EventType.SUBMIT)
  }
}

const handleChange = (form: HTMLFormElement) => {
  const formName = form.getAttribute(globalConfig.formSelector)

  if (formName === null) return

  const inputContainers = form.querySelectorAll(
    `[${globalConfig.inputSelector}]`,
  )

  const inputs = getInputs(inputContainers)

  inputs.forEach((input, index) => {
    input[index]?.addEventListener('change', () => {
      const formData = prepareFormData(inputs)

      const result = validateForm(formName, formData)

      if (result === true) return

      const inputName = input.parentElement?.getAttribute(
        `${globalConfig.inputSelector}`,
      )

      const inputContainer = form.querySelectorAll(
        `[${globalConfig.inputSelector}=${inputName}]`,
      )

      handleErrors(inputContainer, result, EventType.CHANGE)
    })
  })
}

const getInputs = (inputContainers: NodeListOf<Element>) => {
  let inputs: Record<string, HTMLInputElement>[] = []

  inputContainers.forEach((inputContainer) => {
    const key = inputContainer.getAttribute(globalConfig.inputSelector)
    const input = inputContainer.querySelector(
      'input, textarea, select',
    ) as HTMLInputElement

    if (input === null || key === null) return

    inputs = [...inputs, { [key]: input }]
  })

  return inputs
}

const prepareFormData = (inputs: Record<string, HTMLInputElement>[]) => {
  let formData: Record<string, unknown> = {}

  inputs.map((input) => {
    if (Object.values(input)[0]?.type === 'checkbox') {
      return (formData = {
        ...formData,
        [Object.keys(input)[0]]: Object.values(input)[0]?.checked?.toString(),
      })
    }

    formData = {
      ...formData,
      [Object.keys(input)[0]]: Object.values(input)[0]?.value,
    }
  })

  return formData
}

const validateForm = (formName: string, formData: Record<string, unknown>) => {
  const result = formSchema?.[formName as TSchemasKey].safeParse(formData)

  if (!result.success) {
    return result.error.issues
  }

  return true
}

const handleErrors = (
  inputContainers: NodeListOf<Element>,
  result: ZodIssue[],
  eventType: EventType,
) => {
  inputContainers.forEach((inputContainer) => {
    const inputName = inputContainer.getAttribute(
      `${globalConfig.inputSelector}`,
    )

    const isInvalid = result.some((r) => r.path[0] === inputName)
    const errorIndex = result.findIndex((r) => r.path[0] === inputName)

    if (!isInvalid) {
      // Clear error
      return setError(inputContainer, '')
    }

    if (!inputContainer.querySelector(`.${globalConfig.errorClass}`)) {
      inputContainer
        .appendChild(document.createElement('div'))
        .classList.add(globalConfig.errorClass)
    }

    if (eventType === EventType.SUBMIT && errorIndex === 0) {
      inputContainer.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest',
      })
    }

    if (globalConfig.errorShow === 'first' && errorIndex === 0) {
      return setError(inputContainer, result[errorIndex].message)
    }

    if (globalConfig.errorShow === 'all') {
      return setError(inputContainer, result[errorIndex].message)
    }
  })
}

const setError = (inputContainer: Element, message: string) => {
  const elError = inputContainer.querySelector(`.${globalConfig.errorClass}`)

  if (!elError) return

  if (globalConfig.errorType === 'tooltip') {
    const instance = tippy(inputContainer, {
      content: `<div class="tooltip-error">${errorSvg} ${message}</div>`,
      allowHTML: true,
      trigger: 'manual',
      theme: 'material',
      animation: 'scale',
      placement: 'bottom',
      offset: [0, 10],
    })

    if (message !== '') {
      return instance.show()
    }

    instance.hide()
  }

  if (globalConfig.errorType === 'text') {
    return (elError.innerHTML = message)
  }
}

const clearErrors = (inputContainers: NodeListOf<Element>) => {
  inputContainers.forEach((inputContainer) => {
    setError(inputContainer, '')
  })
}

const isFormElement = (form: Element): form is HTMLFormElement => {
  if (form instanceof HTMLFormElement) {
    return true
  }

  return false
}

window.addEventListener('load', () => {
  const forms = document.querySelectorAll(`[${globalConfig.formSelector}]`)

  forms.forEach((form) => {
    if (!isFormElement(form)) return

    handleSubmit(form)
    handleChange(form)
  })
})
